// Navbar3.js - Contains functions for creating a modern Navbar section with social icons and contact info
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a complete modern Navbar section with dropdown functionality, social icons and contact info
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the navbar is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The navbar elements
 */
export const createNavbarElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const navbarSectionClass = 'navbar3-section';
  const navbarTopBarClass = 'navbar3-topbar';
  const navbarTopBarContainerClass = 'navbar3-topbar-container';
  const navbarSocialIconsClass = 'navbar3-social-icons';
  const navbarSocialIconClass = 'navbar3-social-icon';
  const navbarContactInfoClass = 'navbar3-contact-info';
  const navbarContactItemClass = 'navbar3-contact-item';
  const navbarContactIconClass = 'navbar3-contact-icon';
  const navbarContactTextClass = 'navbar3-contact-text';
  const navbarContactTitleClass = 'navbar3-contact-title';
  const navbarContactValueClass = 'navbar3-contact-value';
  const navbarMainClass = 'navbar3-main';
  const navbarContainerClass = 'navbar3-container';
  const navbarLogoClass = 'navbar3-logo';
  const navbarMenuClass = 'navbar3-menu';
  const navbarItemClass = 'navbar3-item';
  const navbarLinkClass = 'navbar3-link';
  const navbarSubmenuClass = 'navbar3-submenu';
  const navbarSubmenuItemClass = 'navbar3-submenu-item';
  const navbarSubmenuLinkClass = 'navbar3-submenu-link';
  const navbarContactBtnClass = 'navbar3-contact-btn';
  const navbarMobileToggleClass = 'navbar3-mobile-toggle';
  const navbarMobileToggleIconClass = 'navbar-mobile-toggle-icon';
  const navbarActiveSubmenuClass = 'active-submenu';
  const navbarMobileMenuShowClass = 'navbar-mobilemenu-show';
  const navbarLinkArrowClass = 'navbar-link-arrow';
  const navbarLinkArrowUpClass = 'navbar-link-arrow-up';

  // Desktop Styles (default)
  const navbarSectionStyles = {
    position: 'relative',
    width: '100%',
    zIndex: '100'
  };

  const navbarTopBarStyles = {
    backgroundColor: '#F8F9FA',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '0',
    paddingRight: '0',
    borderBottom: '1px solid #EAEAEA'
  };

  const navbarTopBarContainerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    marginTop: '0',
    marginBottom: '0',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: '20px',
    paddingRight: '20px',
    paddingTop: '0',
    paddingBottom: '0'
  };

  const navbarSocialIconsStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  };

  const navbarSocialIconStyles = {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F1F1F1',
    borderRadius: '50%',
    transition: 'all 0.3s ease',
    overflow: 'hidden'
  };

  const navbarContactInfoStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '25px'
  };

  const navbarContactItemStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    textDecoration: 'none'
  };

  const navbarContactIconStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#78A84F',
    width: '18px',
    height: '18px'
  };

  const navbarContactTextStyles = {
    display: 'flex',
    flexDirection: 'column',
    color: '#666'
  };

  const navbarContactTitleStyles = {
    fontSize: '14px',
    fontWeight: '600',
    color: '#666',
    marginBottom: '2px',
    lineHeight: '1.2'
  };

  const navbarContactValueStyles = {
    fontSize: '14px',
    fontWeight: '400',
    color: '#666',
    lineHeight: '1.2'
  };

  const navbarMainStyles = {
    backgroundColor: '#FFFFFF',
    paddingTop: '15px',
    paddingBottom: '15px',
    paddingLeft: '0',
    paddingRight: '0',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
  };

  const navbarContainerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    marginTop: '0',
    marginBottom: '0',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: '20px',
    paddingRight: '20px',
    paddingTop: '0',
    paddingBottom: '0'
  };

  const navbarLogoStyles = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '26px',
    fontWeight: '700',
    color: '#333333',
    textDecoration: 'none'
  };

  const navbarMenuStyles = {
    display: 'flex',
    listStyle: 'none',
    // margin: '0',
    // padding: '0',
    gap: '30px'
  };

  const navbarItemStyles = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  };

  const navbarLinkStyles = {
    color: '#333333',
    textDecoration: 'none',
    fontSize: '15px',
    fontWeight: '500',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '0',
    paddingRight: '0',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap',
    display: 'flex',
    alignItems: 'center',
    gap: '5px'
  };

  const navbarLinkArrowStyles = {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    marginLeft: '4px',
    backgroundImage: 'url("https://cdn-icons-png.flaticon.com/512/60/60995.png")',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    transition: 'transform 0.3s ease',
    opacity: '0.7'
  };

  const navbarLinkArrowUpStyles = {
    transform: 'rotate(180deg)'
  };

  const navbarSubmenuStyles = {
    position: 'absolute',
    top: '100%',
    left: '0',
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    borderRadius: '4px',
    minWidth: '200px',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '0',
    paddingRight: '0',
    opacity: '0',
    visibility: 'hidden',
    transform: 'translateY(10px)',
    transition: 'all 0.3s ease',
    zIndex: '200'
  };

  const navbarActiveSubmenuStyles = {
    opacity: '1',
    visibility: 'visible',
    transform: 'translateY(0)'
  };

  const navbarSubmenuItemStyles = {
    display: 'block',
    margin: '0'
  };

  const navbarSubmenuLinkStyles = {
    display: 'block',
    color: '#333333',
    textDecoration: 'none',
    fontSize: '14px',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '16px',
    paddingRight: '16px',
    transition: 'all 0.2s ease'
  };

  const navbarContactBtnStyles = {
    backgroundColor: '#242D3C',
    color: '#ffffff',
    paddingTop: '12px',
    paddingBottom: '12px',
    paddingLeft: '24px',
    paddingRight: '24px',
    borderRadius: '4px',
    fontWeight: '600',
    fontSize: '14px',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    border: 'none',
    cursor: 'pointer',
    display: 'inline-block'
  };

  const navbarMobileToggleStyles = {
    display: 'none',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '8px',
    paddingRight: '8px',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const navbarMobileToggleIconStyles = {
    width: '24px',
    height: '24px',
    display: 'inline-block',
    backgroundImage: 'url("https://cdn-icons-png.flaticon.com/512/5358/5358649.png")',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    transition: 'transform 0.2s ease'
  };

  // Define the mobile menu show class styles
  const navbarMobileMenuShowStyles = {
    display: 'flex'
  };

  // Tablet & Mobile Styles
  const tabletNavbarMenuStyles = {
    ...navbarMenuStyles,
    justifyContent: 'center',
    marginTop: '0',
    marginBottom: '15px',
    marginLeft: '0',
    marginRight: '0',
    gap: '15px'
  };

  const tabletNavbarMainStyles = {
    ...navbarMainStyles,
    paddingTop: '0px',
    paddingBottom: '0px'
  };

  const tabletNavbarContactInfoStyles = {
    ...navbarContactInfoStyles,
    gap: '15px',
    fontSize: '13px'
  };

  const tabletNavbarTopBarContainerStyles = {
    ...navbarTopBarContainerStyles,
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const tabletNavbarSocialIconsStyles = {
    ...navbarSocialIconsStyles,
    gap: '10px'
  };

  const tabletNavbarContactItemStyles = {
    ...navbarContactItemStyles,
    gap: '5px'
  };

  const tabletNavbarContactTitleStyles = {
    ...navbarContactTitleStyles,
    fontSize: '12px'
  };

  const tabletNavbarContactValueStyles = {
    ...navbarContactValueStyles,
    fontSize: '12px'
  };

  const tabletNavbarContainerStyles = {
    ...navbarContainerStyles,
    // padding: '0 15px',
    paddingTop: '0px',
    paddingBottom: '0px',
    paddingLeft: '15px',
    paddingRight: '15px',
    flexDirection: 'column',
    alignItems: 'center'
  };

  const tabletNavbarLogoStyles = {
    ...navbarLogoStyles,
    fontSize: '22px',
    marginTop: '0',
    marginBottom: '15px',
    marginLeft: '0',
    marginRight: '0'
  };

  const tabletNavbarLinkStyles = {
    ...navbarLinkStyles,
    fontSize: '14px',
    padding: '6px 0'
  };

  const tabletNavbarContactBtnStyles = {
    ...navbarContactBtnStyles,
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '20px',
    paddingRight: '20px',
    fontSize: '13px',
    display: 'none'
  };

  const mobileNavbarTopBarContainerStyles = {
    ...tabletNavbarTopBarContainerStyles,
    flexDirection: 'column',
    gap: '10px',
    alignItems: 'center',
    paddingTop: '0px',
    paddingBottom: '0px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const mobileNavbarContactInfoStyles = {
    ...navbarContactInfoStyles,
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    display: 'none'
  };

  const mobileNavbarContainerStyles = {
    ...tabletNavbarContainerStyles,
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    flexDirection: 'row',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '15px',
    paddingRight: '15px',
    position: 'relative'
  };

  const mobileNavbarMenuStyles = {
    display: 'none',
    position: 'absolute',
    top: '100%',
    left: '0',
    right: '0',
    flexDirection: 'column',
    width: '100%',
    gap: '5px',
    marginTop: '0',
    marginBottom: '0',
    marginLeft: '0',
    marginRight: '0',
    backgroundColor: '#fff',
    zIndex: '150',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    paddingTop: '10px',
    paddingBottom: '10px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const mobileNavbarMobileToggleStyles = {
    ...navbarMobileToggleStyles,
    display: 'block'
  };

  const mobileNavbarItemStyles = {
    ...navbarItemStyles,
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    borderBottom: '1px solid #eee',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '0',
    paddingRight: '0'
  };

  const mobileNavbarSubmenuStyles = {
    ...navbarSubmenuStyles,
    position: 'relative',
    top: '0',
    boxShadow: 'none',
    width: '100%',
    maxHeight: '0',
    overflow: 'hidden',
    padding: '0',
    transition: 'max-height 0.3s ease',
    opacity: '1',
    visibility: 'visible',
    transform: 'none'
  };

  const mobileNavbarActiveSubmenuStyles = {
    maxHeight: '500px',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '15px',
    paddingRight: '0'
  };

  const mobileNavbarContactBtnStyles = {
    ...tabletNavbarContactBtnStyles,
    width: '100%',
    textAlign: 'center',
    marginTop: '10px',
    marginBottom: '0',
    marginLeft: '0',
    marginRight: '0',
    paddingTop: '12px',
    paddingBottom: '12px',
    paddingLeft: '20px',
    paddingRight: '20px'
  };

  const mobileNavbarLogoStyles = {
    ...tabletNavbarLogoStyles,
    fontSize: '20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(navbarSectionClass, navbarSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarTopBarClass, navbarTopBarStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarTopBarContainerClass, navbarTopBarContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarSocialIconsClass, navbarSocialIconsStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarSocialIconClass, navbarSocialIconStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarContactInfoClass, navbarContactInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarContactItemClass, navbarContactItemStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarContactIconClass, navbarContactIconStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarContactTextClass, navbarContactTextStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarContactTitleClass, navbarContactTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarContactValueClass, navbarContactValueStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarMainClass, navbarMainStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarContainerClass, navbarContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarLogoClass, navbarLogoStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarMenuClass, navbarMenuStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarItemClass, navbarItemStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarLinkClass, navbarLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarLinkArrowClass, navbarLinkArrowStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarLinkArrowUpClass, navbarLinkArrowUpStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarSubmenuClass, navbarSubmenuStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarSubmenuItemClass, navbarSubmenuItemStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarSubmenuLinkClass, navbarSubmenuLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarContactBtnClass, navbarContactBtnStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarMobileToggleClass, navbarMobileToggleStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarMobileToggleIconClass, navbarMobileToggleIconStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarActiveSubmenuClass, navbarActiveSubmenuStyles);
    newRegistry[DEVICES.DESKTOP].set(navbarMobileMenuShowClass, navbarMobileMenuShowStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(navbarMenuClass, tabletNavbarMenuStyles);
    newRegistry[DEVICES.TABLET].set(navbarContactInfoClass, tabletNavbarContactInfoStyles);
    newRegistry[DEVICES.TABLET].set(navbarTopBarContainerClass, tabletNavbarTopBarContainerStyles);
    newRegistry[DEVICES.TABLET].set(navbarSocialIconsClass, tabletNavbarSocialIconsStyles);
    newRegistry[DEVICES.TABLET].set(navbarContactItemClass, tabletNavbarContactItemStyles);
    newRegistry[DEVICES.TABLET].set(navbarContactTitleClass, tabletNavbarContactTitleStyles);
    newRegistry[DEVICES.TABLET].set(navbarContactValueClass, tabletNavbarContactValueStyles);
    newRegistry[DEVICES.TABLET].set(navbarContainerClass, tabletNavbarContainerStyles);
    newRegistry[DEVICES.TABLET].set(navbarLogoClass, tabletNavbarLogoStyles);
    newRegistry[DEVICES.TABLET].set(navbarLinkClass, tabletNavbarLinkStyles);
    newRegistry[DEVICES.TABLET].set(navbarContactBtnClass, tabletNavbarContactBtnStyles);
    newRegistry[DEVICES.TABLET].set(navbarMobileMenuShowClass, navbarMobileMenuShowStyles);
    newRegistry[DEVICES.TABLET].set(navbarMainClass, tabletNavbarMainStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(navbarTopBarContainerClass, mobileNavbarTopBarContainerStyles);
    newRegistry[DEVICES.MOBILE].set(navbarContactInfoClass, mobileNavbarContactInfoStyles);
    newRegistry[DEVICES.MOBILE].set(navbarContainerClass, mobileNavbarContainerStyles);
    newRegistry[DEVICES.MOBILE].set(navbarMenuClass, mobileNavbarMenuStyles);
    newRegistry[DEVICES.MOBILE].set(navbarMobileToggleClass, mobileNavbarMobileToggleStyles);
    newRegistry[DEVICES.MOBILE].set(navbarMobileToggleIconClass, navbarMobileToggleIconStyles);
    newRegistry[DEVICES.MOBILE].set(navbarItemClass, mobileNavbarItemStyles);
    newRegistry[DEVICES.MOBILE].set(navbarSubmenuClass, mobileNavbarSubmenuStyles);
    newRegistry[DEVICES.MOBILE].set(navbarActiveSubmenuClass, mobileNavbarActiveSubmenuStyles);
    newRegistry[DEVICES.MOBILE].set(navbarMobileMenuShowClass, navbarMobileMenuShowStyles);
    newRegistry[DEVICES.MOBILE].set(navbarContactBtnClass, mobileNavbarContactBtnStyles);
    newRegistry[DEVICES.MOBILE].set(navbarLogoClass, mobileNavbarLogoStyles);
    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    navbarSectionClass,
    navbarTopBarClass,
    navbarTopBarContainerClass,
    navbarSocialIconsClass,
    navbarSocialIconClass,
    navbarContactInfoClass,
    navbarContactItemClass,
    navbarContactIconClass,
    navbarContactTextClass,
    navbarContactTitleClass,
    navbarContactValueClass,
    navbarMainClass,
    navbarContainerClass,
    navbarLogoClass,
    navbarMenuClass,
    navbarItemClass,
    navbarLinkClass,
    navbarLinkArrowClass,
    navbarLinkArrowUpClass,
    navbarSubmenuClass,
    navbarSubmenuItemClass,
    navbarSubmenuLinkClass,
    navbarContactBtnClass,
    navbarMobileToggleClass,
    navbarMobileToggleIconClass,
    navbarActiveSubmenuClass,
    navbarMobileMenuShowClass,
  ]));

  // Generate IDs for all elements
  const navbarSectionId = Date.now();
  const navbarTopBarId = navbarSectionId + 1;
  const navbarTopBarContainerId = navbarSectionId + 2;
  const navbarSocialIconsId = navbarSectionId + 3;
  const navbarContactInfoId = navbarSectionId + 4;
  const navbarMainId = navbarSectionId + 5;
  const navbarContainerId = navbarSectionId + 6;
  const navbarLogoId = navbarSectionId + 7;
  const navbarMenuId = navbarSectionId + 8;
  const navbarContactBtnId = navbarSectionId + 9;
  const navbarMobileToggleId = navbarSectionId + 10;

  // Social icons data
  const socialIcons = [
    { 
      name: 'Facebook', 
      imageSrc: 'https://cdn-icons-png.flaticon.com/512/20/20673.png',
      link: '#' 
    },
    { 
      name: 'Twitter', 
      imageSrc: 'https://cdn-icons-png.flaticon.com/512/25/25347.png', 
      link: '#' 
    },
    { 
      name: 'Instagram', 
      imageSrc: 'https://cdn-icons-png.flaticon.com/512/87/87390.png', 
      link: '#' 
    },
    { 
      name: 'Google', 
      imageSrc: 'https://cdn-icons-png.flaticon.com/512/1946/1946417.png', 
      link: '#' 
    }
  ];

  // Contact info data
  const contactInfo = [
    { 
      name: 'Call Agent', 
      iconSrc: 'https://cdn-icons-png.flaticon.com/128/455/455705.png',
      title: 'Call Agent',
      value: '92 888 000 2222',
      link: 'tel:92888000222'
    },
    { 
      name: 'Send Email', 
      iconSrc: 'https://cdn-icons-png.flaticon.com/128/561/561127.png',
      title: 'Send Email',
      value: 'contact@pbminfotech.com',
      link: 'mailto:contact@pbminfotech.com'
    }
  ];

  // Create social icons elements
  let currentId = navbarMobileToggleId + 1;
  const socialIconElements = [];

  socialIcons.forEach(social => {
    const socialId = currentId++;
    const socialImgId = currentId++;
    
    socialIconElements.push({
      id: socialId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: `${social.name} Icon`,
      hierarchyName: `${social.name} Icon`,
      parentId: navbarSocialIconsId,
      className: navbarSocialIconClass,
      styles: {},
      children: [socialImgId],
      text: '',
      attributes: {
        'data-element': 'navbar-social-icon',
        'href': social.link,
        'aria-label': social.name
      }
    });

    socialIconElements.push({
      id: socialImgId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: `${social.name} Image`,
      hierarchyName: `${social.name} Image`,
      parentId: socialId,
      className: '',
      styles: {
        width: '16px',
        height: '16px'
      },
      children: [],
      text: '',
      src: social.imageSrc,
      attributes: {
        'data-element': 'navbar-social-icon-image',
        'alt': social.name
      }
    });
  });

  // Create contact info elements
  const contactInfoElements = [];

  contactInfo.forEach(contact => {
    const contactItemId = currentId++;
    const contactIconId = currentId++;
    const contactTextId = currentId++;
    const contactTitleId = currentId++;
    const contactValueId = currentId++;
    
    contactInfoElements.push({
      id: contactItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: `${contact.name} Item`,
      hierarchyName: `${contact.name} Item`,
      parentId: navbarContactInfoId,
      className: navbarContactItemClass,
      styles: {},
      children: [contactIconId, contactTextId],
      text: '',
      attributes: {
        'data-element': 'navbar-contact-item',
        'href': contact.link
      }
    });

    contactInfoElements.push({
      id: contactIconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: `${contact.name} Icon`,
      hierarchyName: `${contact.name} Icon`,
      parentId: contactItemId,
      className: navbarContactIconClass,
      styles: {},
      children: [],
      text: '',
      src: contact.iconSrc,
      attributes: {
        'data-element': 'navbar-contact-icon',
        'alt': `${contact.name} icon`
      }
    });

    contactInfoElements.push({
      id: contactTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `${contact.name} Text`,
      hierarchyName: `${contact.name} Text`,
      parentId: contactItemId,
      className: navbarContactTextClass,
      styles: {},
      children: [contactTitleId, contactValueId],
      text: '',
      attributes: {
        'data-element': 'navbar-contact-text'
      }
    });

    contactInfoElements.push({
      id: contactTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: `${contact.name} Title`,
      hierarchyName: `${contact.name} Title`,
      parentId: contactTextId,
      className: navbarContactTitleClass,
      styles: {},
      children: [],
      text: contact.title,
      attributes: {
        'data-element': 'navbar-contact-title'
      }
    });

    contactInfoElements.push({
      id: contactValueId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: `${contact.name} Value`,
      hierarchyName: `${contact.name} Value`,
      parentId: contactTextId,
      className: navbarContactValueClass,
      styles: {},
      children: [],
      text: contact.value,
      attributes: {
        'data-element': 'navbar-contact-value'
      }
    });
  });

  // Menu items (navigation links)
  const menuItems = [
    { name: 'Home', link: '#', hasSubmenu: true, submenuItems: [
      { name: 'Homepage 1', link: '#' },
      { name: 'Homepage 2', link: '#' },
      { name: 'Homepage 3', link: '#' }
    ]},
    { name: 'Pages', link: '#', hasSubmenu: true, submenuItems: [
      { name: 'Page 1', link: '#' },
      { name: 'Page 2', link: '#' },
      { name: 'Page 3', link: '#' }
    ]},
    { name: 'Services', link: '#', hasSubmenu: false },
    { name: 'Projects', link: '#', hasSubmenu: false },
    { name: 'Blog', link: '#', hasSubmenu: true, submenuItems: [
      { name: 'Blog Post 1', link: '#' },
      { name: 'Blog Post 2', link: '#' },
      { name: 'Blog Post 3', link: '#' }
    ]},
    { name: 'Contact', link: '#', hasSubmenu: false }
  ];

  // Create navigation menu elements
  const menuItemElements = [];

  menuItems.forEach((item, index) => {
    const menuItemId = currentId++;
    const linkId = currentId++;
    let submenuId = null;
    let arrowIconId = null;
    const submenuItems = [];

    if (item.hasSubmenu) {
      submenuId = currentId++;
      arrowIconId = currentId++;
      
      // Create submenu items
      item.submenuItems.forEach(submenuItem => {
        const submenuItemId = currentId++;
        const submenuLinkId = currentId++;
        
        submenuItems.push({
          id: submenuItemId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'li',
          name: `${item.name} Submenu Item - ${submenuItem.name}`,
          hierarchyName: `${item.name} Submenu Item - ${submenuItem.name}`,
          parentId: submenuId,
          className: navbarSubmenuItemClass,
          styles: {},
          children: [submenuLinkId],
          text: '',
          attributes: {
            'data-element': 'navbar-submenu-item'
          }
        });

        submenuItems.push({
          id: submenuLinkId,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'link',
          name: `${submenuItem.name} Link`,
          hierarchyName: `${submenuItem.name} Link`,
          parentId: submenuItemId,
          className: navbarSubmenuLinkClass,
          styles: {},
          children: [],
          text: submenuItem.name,
          attributes: {
            'data-element': 'navbar-submenu-link',
            'href': submenuItem.link
          }
        });
      });
    }

    // Create menu item element
    menuItemElements.push({
      id: menuItemId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'li',
      name: `${item.name} Menu Item`,
      hierarchyName: `${item.name} Menu Item`,
      parentId: navbarMenuId,
      className: navbarItemClass,
      styles: {},
      children: item.hasSubmenu ? [linkId, submenuId] : [linkId],
      text: '',
      attributes: {
        'data-element': 'navbar-item',
        'data-has-submenu': item.hasSubmenu.toString()
      },
      interactions: item.hasSubmenu ? {
        events: [
          {
            id: Date.now() + index,
            type: 'hover',
            action: 'toggleClass',
            direction: 'in',
            label: `Hover In: Add active class to submenu`,
            value: {
              targetId: submenuId,
              transitionDuration: '0.3s',
              className: navbarActiveSubmenuClass
            }
          },
          {
            id: Date.now() + index + 1000,
            type: 'hover',
            action: 'toggleClass',
            direction: 'out',
            label: `Hover Out: Remove active class from submenu`,
            value: {
              targetId: submenuId,
              className: navbarActiveSubmenuClass,
              remove: true
            }
          },
          {
            id: Date.now() + index + 2000,
            type: 'hover',
            action: 'toggleClass',
            direction: 'in',
            label: `Hover In: Rotate arrow icon`,
            value: {
              targetId: arrowIconId,
              className: navbarLinkArrowUpClass
            }
          },
          {
            id: Date.now() + index + 3000,
            type: 'hover',
            action: 'toggleClass',
            direction: 'out',
            label: `Hover Out: Restore arrow icon`,
            value: {
              targetId: arrowIconId,
              className: navbarLinkArrowUpClass,
              remove: true
            }
          }
        ]
      } : {}
    });

    // Create menu item link and arrow icon if needed
    if (item.hasSubmenu) {
      menuItemElements.push({
        id: linkId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'link',
        name: `${item.name} Link`,
        hierarchyName: `${item.name} Link`,
        parentId: menuItemId,
        className: navbarLinkClass,
        styles: {},
        children: [arrowIconId],
        text: item.name,
        attributes: {
          'data-element': 'navbar-link',
          'href': item.link
        }
      });

      // Create arrow icon element
      menuItemElements.push({
        id: arrowIconId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `${item.name} Arrow Icon`,
        hierarchyName: `${item.name} Arrow Icon`,
        parentId: linkId,
        className: navbarLinkArrowClass,
        styles: {},
        children: [],
        text: '',
        attributes: {
          'data-element': 'navbar-link-arrow',
          'aria-hidden': 'true'
        }
      });
    } else {
      menuItemElements.push({
        id: linkId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'link',
        name: `${item.name} Link`,
        hierarchyName: `${item.name} Link`,
        parentId: menuItemId,
        className: navbarLinkClass,
        styles: {},
        children: [],
        text: item.name,
        attributes: {
          'data-element': 'navbar-link',
          'href': item.link
        }
      });
    }

    // Create submenu if needed
    if (item.hasSubmenu) {
      // Create submenu container
      menuItemElements.push({
        id: submenuId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'ul',
        name: `${item.name} Submenu`,
        hierarchyName: `${item.name} Submenu`,
        parentId: menuItemId,
        className: navbarSubmenuClass,
        styles: {},
        children: submenuItems.filter(item => item.type === 'li').map(item => item.id),
        text: '',
        attributes: {
          'data-element': 'navbar-submenu'
        }
      });

      // Add submenu items to the elements array
      menuItemElements.push(...submenuItems);
    }
  });

  const elementIdsForMenu = menuItemElements
    .filter(element => element.parentId === navbarMenuId)
    .map(element => element.id);

  // Create base navbar elements
  const baseElements = [
    // Main Navbar Section
    {
      id: navbarSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'nav',
      name: 'Navbar Section',
      hierarchyName: 'Navbar Section',
      parentId: null,
      className: navbarSectionClass,
      styles: {},
      children: [navbarTopBarId, navbarMainId],
      text: '',
      attributes: {
        'data-element': 'navbar-section',
        'data-component': 'nav',
        'data-section-type': 'navbar',
        'data-page-id': currentPageId
      }
    },
    // Top Bar
    {
      id: navbarTopBarId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Navbar Top Bar',
      hierarchyName: 'Navbar Top Bar',
      parentId: navbarSectionId,
      className: navbarTopBarClass,
      styles: {},
      children: [navbarTopBarContainerId],
      text: '',
      attributes: {
        'data-element': 'navbar-topbar'
      }
    },
    // Top Bar Container
    {
      id: navbarTopBarContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Navbar Top Bar Container',
      hierarchyName: 'Navbar Top Bar Container',
      parentId: navbarTopBarId,
      className: navbarTopBarContainerClass,
      styles: {},
      children: [navbarSocialIconsId, navbarContactInfoId],
      text: '',
      attributes: {
        'data-element': 'navbar-topbar-container'
      }
    },
    // Social Icons Container
    {
      id: navbarSocialIconsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Navbar Social Icons',
      hierarchyName: 'Navbar Social Icons',
      parentId: navbarTopBarContainerId,
      className: navbarSocialIconsClass,
      styles: {},
      children: socialIconElements.filter(el => el.type === 'link').map(el => el.id),
      text: '',
      attributes: {
        'data-element': 'navbar-social-icons'
      }
    },
    // Contact Info Container
    {
      id: navbarContactInfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Navbar Contact Info',
      hierarchyName: 'Navbar Contact Info',
      parentId: navbarTopBarContainerId,
      className: navbarContactInfoClass,
      styles: {},
      children: contactInfoElements.filter(el => el.parentId === navbarContactInfoId).map(el => el.id),
      text: '',
      attributes: {
        'data-element': 'navbar-contact-info'
      }
    },
    // Main Navbar
    {
      id: navbarMainId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'nav',
      name: 'Navbar Main',
      hierarchyName: 'Navbar Main',
      parentId: navbarSectionId,
      className: navbarMainClass,
      styles: {},
      children: [navbarContainerId],
      text: '',
      attributes: {
        'data-element': 'navbar-main',
        'role': 'navigation',
        'aria-label': 'Main navigation'
      }
    },
    // Main Container
    {
      id: navbarContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Navbar Container',
      hierarchyName: 'Navbar Container',
      parentId: navbarMainId,
      className: navbarContainerClass,
      styles: {},
      children: [navbarLogoId, navbarMenuId, navbarMobileToggleId, navbarContactBtnId],
      text: '',
      attributes: {
        'data-element': 'navbar-container'
      }
    },
    // Logo
    {
      id: navbarLogoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Navbar Logo',
      hierarchyName: 'Navbar Logo',
      parentId: navbarContainerId,
      className: navbarLogoClass,
      styles: {},
      children: [],
      text: 'Solecto',
      attributes: {
        'data-element': 'navbar-logo',
        'href': '#'
      }
    },
    // Contact Button
    {
      id: navbarContactBtnId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Contact Button',
      hierarchyName: 'Contact Button',
      parentId: navbarContainerId,
      className: navbarContactBtnClass,
      styles: {},
      children: [],
      text: 'Contact Us',
      attributes: {
        'data-element': 'navbar-contact-btn',
        'href': '#',
        'role': 'button'
      }
    },
    // Mobile Toggle Button
    {
      id: navbarMobileToggleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Mobile Toggle',
      hierarchyName: 'Mobile Toggle',
      parentId: navbarContainerId,
      className: navbarMobileToggleClass,
      styles: {},
      children: [navbarMobileToggleId + 100], // Add span child
      text: '',
      attributes: {
        'data-element': 'navbar-mobile-toggle',
        'aria-label': 'Toggle navigation menu',
      },
      interactions: {
        events: [
          {
            id: Date.now() + 100,
            type: 'click',
            action: 'toggleClass',
            sequence: 'first',
            label: '1st Click: Toggle Class: navbar-mobilemenu-show on navbar-menu',
            value: {
              targetId: navbarMenuId,
              className: navbarMobileMenuShowClass
            }
          },
          {
            id: Date.now() + 101,
            type: 'click',
            action: 'toggleClass',
            sequence: 'second',
            label: '2nd Click: Toggle Class: navbar-mobilemenu-hide on navbar-menu',
            value: {
              targetId: navbarMenuId,
              className: navbarMobileMenuShowClass
            }
          }
        ]
      }
    },
    // Mobile Toggle Icon
    {
      id: navbarMobileToggleId + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'span',
      name: 'Mobile Toggle Icon',
      hierarchyName: 'Mobile Toggle Icon',
      parentId: navbarMobileToggleId,
      className: navbarMobileToggleIconClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'navbar-mobile-toggle-icon',
        'aria-hidden': 'true'
      }
    },
    // Menu Container
    {
      id: navbarMenuId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'ul',
      name: 'Navbar Menu',
      hierarchyName: 'Navbar Menu',
      parentId: navbarContainerId,
      className: navbarMenuClass,
      styles: {},
      children: elementIdsForMenu,
      text: '',
      attributes: {
        'data-element': 'navbar-menu'
      }
    }
  ];

  // Combine all elements
  const allElements = [
    ...baseElements,
    ...socialIconElements,
    ...contactInfoElements,
    ...menuItemElements
  ];
  
  // Return the created elements
  return {
    navbarSection: allElements[0],
    elements: allElements
  };
};

export default createNavbarElements; 